import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi } from "../api";
import { LinkedDelivery } from "./LinkedDelivery";
import { usePermissions } from "../permissions";
import type { Initiative } from "../types";
import { Card, PageHeader, PageSkeleton, ErrorNotice } from "./Ui";
const categories = [
  ["C", "Strategic growth"],
  ["A", "Regulatory mandates"],
  ["B", "Partner commitments"],
  ["D", "Platform foundations"],
  ["F", "Exploratory / innovations"],
] as const;
export function ProductRegister() {
  const api = useApi();
  const permissions = usePermissions();
  const query = useQuery({
    queryKey: ["initiatives"],
    queryFn: () => api.get<{ items: Initiative[] }>("/initiatives"),
  });
  if (query.isPending) return <PageSkeleton />;
  if (query.isError) return <ErrorNotice error={query.error} />;
  return (
    <div className="page scroll-page product-summary">
      <PageHeader
        title="Product register"
        description="Strategic needs and expected outcomes, grouped by category. Summaries include only initiatives you can access; lifecycle and scoring stay in Initiatives."
      />
      {categories.map(([key, name]) => {
        const items = query.data.items.filter((i) => i.category === key);
        return (
          <Card key={key}>
            <div className="card-header">
              <h2>{name}</h2>
              <span>{items.length} initiatives</span>
            </div>
            <p className="section-help">
              Strategic drivers:{" "}
              {[
                ...new Set(items.map((i) => i.strategicDriver).filter(Boolean)),
              ].join(" · ") || "None recorded"}
            </p>
            {!items.length ? (
              <p>No accessible initiatives in this category.</p>
            ) : (
              <div className="table-wrap">
                <table className="initiative-table">
                  <thead>
                    <tr>
                      <th>Strategic need / request</th>
                      <th>Description</th>
                      <th>Expected outcome</th>
                      <th>Strategic driver</th>
                      {permissions.can("capacity.view") && (
                        <th>Linked delivery</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={i.id}>
                        <td>
                          <Link to={`/initiatives/${i.publicId}`}>
                            {i.name}
                          </Link>
                        </td>
                        <td>{i.description}</td>
                        <td>{i.expectedOutcome || "Not yet recorded"}</td>
                        <td>{i.strategicDriver}</td>
                        {permissions.can("capacity.view") && (
                          <td>
                            <LinkedDelivery initiativeId={i.id} />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
